import React, { MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import caretLeftTokens from '@amzn/meridian-tokens/base/icon/caret-left';
import chevronLeftTokens from '@amzn/meridian-tokens/base/icon/chevron-left-small';
import chevronRightTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import SideMenu, { SideMenuLink } from '@amzn/meridian/side-menu';
import Text from '@amzn/meridian/text';

import { isNil } from 'lodash';
import { useHistory } from 'react-router';
import { LinkConfig, PageStructure, TopLevelLinkConfig } from 'src/components/SideBar/sidebar-types';
import { AppContext } from 'src/contexts/appContext';
import { useCookie } from 'src/hooks/useCookie';

const EXPANDED_COOKIE_DELIMITER = ',';

interface IGenericSideBar {
    identifier: string;
    pageStructure: PageStructure;
    selected?: string;
    isHome?: boolean;
    hiddenLinks?: Set<string>;
}

interface TootipState {
    x: number;
    y: number;
    message: string;
}

export const GenericSideBar: React.FC<IGenericSideBar> = (props) => {
    const history = useHistory();
    const [_expanded, setExpanded] = useCookie<string>(`${props.identifier}SidebarFocus`, '');
    const expanded = useMemo(() => new Set(_expanded.split(EXPANDED_COOKIE_DELIMITER)), [_expanded]);
    const { pageStructure, selected, isHome } = props;
    const { isSideBarClosed, setIsSideBarClosed } = useContext(AppContext);
    const [tooltip, setTooltip] = useState<TootipState>();
    const tooltipStyle = useMemo<React.CSSProperties>(
        () => ({
            position: 'absolute',
            zIndex: 50,
            top: tooltip?.y,
            left: tooltip?.x,
            transform: 'translateY(-50%)' // center the tooltip vertically
        }),
        [tooltip]
    );

    // keeps the sidebar closed on the initial load of the home page
    // if the sidebar is already open, navigating back to the home page
    // will not close the sidebar
    const [initialOpen, setInitialOpen] = useState(!isHome);
    useEffect(() => {
        if (isHome && !initialOpen) {
            setIsSideBarClosed(true);
        }
    }, [isHome, initialOpen]);
    useEffect(() => {
        if (isSideBarClosed) {
            setInitialOpen(true);
        }
    }, [isSideBarClosed]);

    // navigates to the linked page, or expands the section if it has children
    const topLevelOnClick = useCallback(
        (parent: TopLevelLinkConfig) => {
            return () => {
                if (!isNil(parent.href)) {
                    history.push(parent.href);
                } else {
                    if (isSideBarClosed) {
                        setIsSideBarClosed(false);
                        setExpanded((_prev) => {
                            const prev = _prev.split(EXPANDED_COOKIE_DELIMITER);
                            if (!prev.includes(parent.name)) {
                                return [...prev, parent.name].join(EXPANDED_COOKIE_DELIMITER);
                            } else {
                                return _prev;
                            }
                        });
                    } else {
                        setExpanded((_prev) => {
                            const prev = _prev.split(EXPANDED_COOKIE_DELIMITER);
                            if (prev.includes(parent.name)) {
                                return prev.filter((name) => name !== parent.name).join(EXPANDED_COOKIE_DELIMITER);
                            } else {
                                return [...prev, parent.name].join(EXPANDED_COOKIE_DELIMITER);
                            }
                        });
                    }
                }
            };
        },
        [history, isSideBarClosed]
    );

    const shouldRenderLink = useCallback(
        (link: LinkConfig) => {
            return !props.hiddenLinks?.has(link.name) || (link.href === selected && !isNil(selected));
        },
        [selected]
    );

    const isOpen = !isSideBarClosed && initialOpen;

    const onMouseOver = useCallback((e: MouseEvent) => {
        // make sure the target is an element
        const target = e.target;
        if (!(target instanceof Element)) {
            return;
        }

        // all the clickable sidebar items are buttons
        const parentButton = target.closest('button');
        if (isNil(parentButton)) {
            return;
        }

        // get the containing nav to figure out how far to the right we should go
        const parentNav = target.closest('nav') ?? parentButton;

        // get the tooltip text the svg title
        const labelSource = parentButton.querySelector('title');
        const label = labelSource?.firstChild?.textContent;

        // use the parent button to determine y position
        const buttonRect = parentButton.getBoundingClientRect();
        const iconCenterline = buttonRect.y + buttonRect.height / 2;

        const navRect = parentNav.getBoundingClientRect();
        const navEdge = navRect.x + navRect.width;

        setTooltip({
            x: navEdge,
            y: iconCenterline,
            message: label || 'Open Menu'
        });
    }, []);

    const onMouseOut = useCallback((e: MouseEvent) => {
        const target = e.target;
        if (target instanceof Element) {
            setTooltip(undefined);
        }
    }, []);

    return (
        <div style={{ display: 'flex' }} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
            {!isNil(tooltip) && !isOpen && (
                <div style={tooltipStyle}>
                    <Column alignmentVertical={'center'}>
                        <Row alignmentVertical={'center'} spacing={'5px'}>
                            <Icon tokens={caretLeftTokens} />
                            <Box type={'fill'} spacingInset={'200'} role={'tooltip'}>
                                <Text>{tooltip.message}</Text>
                            </Box>
                        </Row>
                    </Column>
                </div>
            )}
            <SideMenu
                open={isOpen}
                type={'skinny'}
                id={'side-menu-2'}
                onClose={() => setIsSideBarClosed(true)}
                onOpen={() => setIsSideBarClosed(false)}
            >
                {pageStructure.filter(shouldRenderLink).map((parent) => (
                    <SideMenuLink
                        onClick={topLevelOnClick(parent)}
                        icon={parent.tokens}
                        open={expanded.has(parent.name) && !isSideBarClosed}
                        selected={!isNil(parent.href) && selected === parent.href}
                    >
                        {parent.name}
                        {parent.children?.filter(shouldRenderLink)?.map((child) => (
                            <SideMenuLink
                                onClick={() => !isNil(child.href) && history.push(child.href)}
                                selected={selected === child.href}
                            >
                                {child.name}
                            </SideMenuLink>
                        ))}
                    </SideMenuLink>
                ))}
            </SideMenu>
        </div>
    );
};
